import React from 'react';
import { Button, Switch, Tag, Tooltip } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { useDataProvider, useNotify, usePermissions } from 'react-admin';
import { ExtendedDataProvider } from '@src/types';
import { humanizeSeconds } from '../Common/Form';
import { IntegrationConfig, RESPONSE_STATE_OPTIONS } from '../Common/Integrations';

export const CHANGE_INSTANCE_STATES = ['PENDING', 'APPROVED', 'ERROR', 'REJECTED'];

const WebhookActions = ({ record, refresh }: { record: any; refresh: () => void }) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const { permissions } = usePermissions();
    const isAdmin = permissions === 'ADMIN';

    const trigger = () => {
        dataProvider
            .createAction('webhooks', { data: {}, id: record.id, verb: 'trigger/' })
            .then(() => {
                notify('Webhook triggered');
                refresh();
            })
            // the API explains why a trigger was refused (inactive, cooling down,
            // rate limited) — show that rather than a generic failure
            .catch((error: any) => notify(error.body?.trigger || 'Could not trigger the webhook', 'error'));
    };

    const setActive = (checked: boolean) => {
        // the API takes a PUT, so every writable field has to travel with the
        // request or it silently reverts to the model default
        dataProvider
            .update('webhooks', {
                id: record.id,
                data: {
                    name: record.name,
                    description: record.description,
                    target_url: record.target_url,
                    authorization: record.authorization,
                    service: record.service?.id,
                    trust_env: record.trust_env,
                    verify: record.verify,
                    timeout: record.timeout,
                    change_instance_state: record.change_instance_state,
                    trigger_wait_time: record.trigger_wait_time,
                    trigger_cooling_time: record.trigger_cooling_time,
                    active: checked,
                },
                previousData: { id: record.id },
            })
            .then(() => {
                notify(checked ? 'Webhook enabled' : 'Webhook paused');
                refresh();
            })
            .catch(() => notify('Could not update the webhook', 'error'));
    };

    return (
        <>
            {isAdmin && (
                <Tooltip title="Send a request now">
                    <Button type="primary" size="small" icon={<ThunderboltOutlined />} onClick={trigger}>
                        Trigger
                    </Button>
                </Tooltip>
            )}
            {isAdmin && (
                <Tooltip title={record.active ? 'Pause this webhook' : 'Enable this webhook'}>
                    <label className={`nio-switch${record.active ? ' nio-switch--on' : ''}`}>
                        <Switch
                            size="small"
                            checked={record.active}
                            onChange={setActive}
                            aria-label={`${record.active ? 'Pause' : 'Enable'} webhook ${record.name}`}
                        />
                        {record.active ? 'Enabled' : 'Disabled'}
                    </label>
                </Tooltip>
            )}
        </>
    );
};

const webhookConfig: IntegrationConfig = {
    resource: 'webhooks',
    basePath: '/webhooks',
    singular: 'Webhook',
    plural: 'Webhooks',
    createLabel: 'New webhook',
    emptyTitle: 'No webhooks yet',
    emptyBody:
        'A webhook lets NetOrca notify your systems the moment a change instance is raised for one of your services, so you can start work without polling.',

    canWrite: (permissions) => permissions === 'ADMIN',

    title: (record) => record.name,
    subtitle: (record) => record.description || undefined,
    status: (record) => (record.active ? { tone: 'good', label: 'Active' } : { tone: 'muted', label: 'Paused' }),

    badges: (record) => (
        <>
            {record.change_instance_state ? (
                <Tag color="blue">On {record.change_instance_state}</Tag>
            ) : (
                <Tag color="blue">On creation</Tag>
            )}
            {!record.verify && <Tag color="orange">TLS unverified</Tag>}
        </>
    ),

    meta: (record) => [
        { label: 'Service', value: record.service?.name },
        { label: 'Target', value: record.target_url, mono: true, title: record.target_url },
        {
            label: 'Batching',
            value: `wait ${humanizeSeconds(record.trigger_wait_time)} · cool-down ${humanizeSeconds(
                record.trigger_cooling_time,
            )}`,
        },
    ],

    Actions: WebhookActions,

    responses: {
        resource: 'webhook_responses',
        columns: [
            {
                label: 'Webhook',
                filters: ['webhook_id'],
                value: (record) => record.webhook?.name || `Webhook #${record.webhook?.id ?? '—'}`,
            },
            {
                label: 'Service',
                filters: ['service_id'],
                value: (record) => record.webhook?.service?.name,
            },
        ],
        filters: [
            {
                name: 'webhook_id',
                label: 'Webhook',
                type: 'select',
                resource: 'webhooks',
                // that endpoint has no /filters/ route, so the options come from
                // the list itself
                optionsFrom: 'list',
            },
            {
                name: 'service_id',
                label: 'Service',
                type: 'select',
                resource: 'services',
            },
            {
                name: 'state',
                label: 'State',
                type: 'select',
                options: RESPONSE_STATE_OPTIONS,
            },
        ],
        emptyText:
            'Responses appear here once a webhook fires, whether it was triggered by a change instance or by hand.',
    },
};

export default webhookConfig;
